import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MFA_METHOD } from '@client-hub/shared/constants'
import { VerifyMfaCodePayload } from '@client-hub/store/api/crs/types'
import { useUserLoginMutation, useVerifyMfaCodeMutation } from '@client-hub/store/api/crs/react'
import MfaAuth, { MfaAuthContentType } from 'components/Mfa/Auth'
import { AuthCodeSchema } from 'schemas/mfa'
import { handleAuthCodeChange } from 'utils/mfa'
import { useMfaNavigateState, useRedirect } from 'hooks'
import ROUTES from '@client-hub/shared/constants/routes'
import { useTranslation } from 'react-i18next'
import { CRS_LOGIN_MUTATION_CACHE_KEY } from 'constants/auth'

const MfaLogin = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [verifyMfaCode, { isSuccess, data }] = useVerifyMfaCodeMutation()
  const [, { reset }] = useUserLoginMutation({ fixedCacheKey: CRS_LOGIN_MUTATION_CACHE_KEY })
  const { uid, sms_status, two_factor_auth, from, isAllowedMfa } = useMfaNavigateState()
  const smsMessage = sms_status?.['message'] || ''

  useRedirect(!isAllowedMfa)

  const content: MfaAuthContentType = {
    showLogo: true,
    back: { name: t('routes.mfaAuth.global.backLogin'), url: ROUTES.LOGIN.BASE, callback: reset },
    placeholder: t('forms.mfaAuth.fields.authCode.placeholder'),
    label: two_factor_auth === MFA_METHOD.AUTHENTICATOR_APP ? t('routes.mfaAuth.auth.labelSetup') : smsMessage,
    switchButton: { name: t('routes.mfaAuth.auth.switchText'), url: ROUTES.LOGIN.MFA.RECOVERY },
  }

  useEffect(() => {
    if (isSuccess && data) {
      navigate(from?.pathname ? from?.pathname : ROUTES.BASE, { replace: true })
    }
  }, [data, from?.pathname, isSuccess, navigate])

  const onSubmit = useCallback(
    (code: string) => {
      const requestData: VerifyMfaCodePayload = {
        uid,
        two_factor_auth,
        totp: code,
      }
      verifyMfaCode(requestData)
    },
    [two_factor_auth, uid, verifyMfaCode],
  )

  if (!isAllowedMfa) return null

  return <MfaAuth content={content} schema={AuthCodeSchema} onChange={handleAuthCodeChange} onSubmit={onSubmit} />
}

export default MfaLogin
